import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, Users, FileText, CreditCard, LogOut, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import NotificationCenter from "@/components/NotificationCenter";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const links = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/tenants", label: "Tenants", icon: Users },
    { to: "/admin/plans", label: "Plans", icon: FileText },
    { to: "/admin/payments", label: "Payments", icon: CreditCard },
  ];

  const SidebarContent = ({ onNavigate }) => (
    <div className="flex flex-col h-full">
      <Link to="/admin" onClick={onNavigate} className="px-6 py-5 border-b border-slate-200 flex items-center gap-2" data-testid="admin-logo">
        <div className="w-8 h-8 rounded bg-slate-900 grid place-items-center text-white font-bold">R</div>
        <div>
          <div className="font-display text-base font-bold leading-none">Rental.SaaS</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">App Owner</div>
        </div>
      </Link>
      <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
        {links.map((l) => {
          const Icon = l.icon;
          return (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={onNavigate}
              data-testid={`admin-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
            >
              <Icon className="h-4 w-4" /> {l.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-200">
        <div className="text-xs text-slate-500 mb-2">Signed in as</div>
        <div className="text-sm font-medium text-slate-900 mb-3" data-testid="admin-user-name">{user?.name}</div>
        <Button variant="outline" size="sm" className="w-full" data-testid="admin-logout-btn" onClick={() => { logout(); nav("/admin/login"); }}>
          <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 bg-white border-r border-slate-200 flex-col">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-white" data-testid="admin-mobile-drawer">
          <SidebarContent onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>

      <main className="flex-1 overflow-x-hidden min-w-0">
        <div className="border-b border-slate-200 bg-white px-3 sm:px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 lg:hidden">
            <SheetTriggerButton onOpen={() => setOpen(true)} />
            <Link to="/admin" className="flex items-center gap-2" data-testid="admin-mobile-logo">
              <div className="w-7 h-7 rounded bg-slate-900 grid place-items-center text-white font-bold text-xs">R</div>
              <div className="font-display text-sm font-bold leading-none">Rental.SaaS</div>
            </Link>
          </div>
          <div className="ml-auto">
            <NotificationCenter accentColor="#0F172A" />
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}

function SheetTriggerButton({ onOpen }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="lg:hidden"
      onClick={onOpen}
      data-testid="admin-menu-toggle"
      aria-label="Open menu"
    >
      <Menu className="h-5 w-5" />
    </Button>
  );
}
